import Phaser from 'phaser';
import { onlineSession, type OnlineState } from './online';
import { resolveQaAutoJoin } from './qaAutoJoin';
import {installAccountLobby} from './accountLobby';

function element<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Missing lobby element #${id}`);
  return node as T;
}


export function installOnlineLobby(game: Phaser.Game): void {
  const panel = element<HTMLDivElement>('online-lobby');
  const nameInput = element<HTMLInputElement>('online-player-name');
  const joinButton = element<HTMLButtonElement>('online-join');
  const offlineButton = element<HTMLButtonElement>('offline-play');
  const status = element<HTMLDivElement>('online-lobby-status');
  const networkBadge = element<HTMLDivElement>('network-status');
  const tutorial = element<HTMLDivElement>('first-play-guide');
  const qaAutoJoin = resolveQaAutoJoin(window.location);

  nameInput.value = qaAutoJoin?.name ?? localStorage.getItem('phi-siam-name') ?? 'ศิษย์ใหม่';
  installAccountLobby(panel,nameInput,!!qaAutoJoin||new URLSearchParams(window.location.search).get('qa')==='game');

  const setBusy = (busy: boolean): void => {
    joinButton.disabled = busy;
    offlineButton.disabled = busy;
  };

  const enterGameplay = (): void => {
    document.body.classList.remove('lobby-active');
    panel.classList.remove('visible');
    tutorial.classList.add('visible');
    window.setTimeout(() => tutorial.classList.remove('visible'), 9000);
    game.scene.stop('lobby');
    game.scene.start('ban-bang-phi');
  };

  const join = async (): Promise<void> => {
    const name = nameInput.value.trim() || 'ศิษย์ใหม่';
    const room = qaAutoJoin?.roomId ?? 'BANG-PHI-01';
    localStorage.setItem('phi-siam-name', name);
    setBusy(true);
    status.textContent = 'กำลังเชื่อมต่อโลกออนไลน์…';
    status.className = 'online-lobby-status connecting';
    try {
      if(!game.scene.isActive('lobby'))await new Promise<void>(resolve=>window.addEventListener('phi-lobby-ready',()=>resolve(),{once:true}));
      await onlineSession.connect(room, name);
      status.textContent = 'เชื่อมต่อแล้ว';
      status.className = 'online-lobby-status online';
      enterGameplay();
    } catch (error) {
      status.textContent = error instanceof Error ? error.message : 'เชื่อมต่อไม่สำเร็จ';
      status.className = 'online-lobby-status error';
    } finally {
      setBusy(false);
    }
  };

  joinButton.addEventListener('click', () => void join());
  offlineButton.addEventListener('click', () => {
    onlineSession.disconnect();
    status.textContent = 'โหมดออฟไลน์ — ความคืบหน้าไม่แชร์กับผู้เล่นอื่น';
    status.className = 'online-lobby-status offline';
    enterGameplay();
  });

  const updateNetworkBadge = (state: Readonly<OnlineState>): void => {
    if (state.status === 'online') {
      const count = state.snapshot?.players.length ?? 1;
      const parts=[['network-state','● ONLINE'],['network-room','โลกผีสยาม'],['network-ping',`${count} คน · ${state.latencyMs===null?'—':state.latencyMs+' ms'}`]];
      networkBadge.replaceChildren(...parts.map(([className,text])=>{const span=document.createElement('span');span.className=className!;span.textContent=text!;return span;}));
      networkBadge.title=`โลกผีสยาม · ผู้เล่น ${count} · Ping ${state.latencyMs??'—'} ms`;
      networkBadge.className = 'network-status online';
      return;
    }
    networkBadge.textContent = '○ OFFLINE';
    networkBadge.className = 'network-status offline';
  };
  onlineSession.subscribe(updateNetworkBadge);

  const observer = new MutationObserver(() => {
    panel.classList.toggle('visible', document.body.classList.contains('lobby-active'));
  });
  observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  panel.classList.toggle('visible', document.body.classList.contains('lobby-active'));

  if (qaAutoJoin) window.setTimeout(() => void join(), 0);
}
